import { useMemo, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, Button, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGetRewardList } from '../../hooks/useReward';
import { useDialogStore } from '../../stores/dialogStore';
import { useUserStore } from '../../stores/userStore';
import RedeemDialog from './components/RedeemDialog';
import RewardDetailCard from './components/RewardDetailCard';
import RewardDetailInfo from './components/RewardDetailInfo';

const RewardDetailPage = () => {
  const { rewardId } = useParams();
  const navigate = useNavigate();

  const userInfo = useUserStore((state) => state.userInfo);
  const companyInfo = useUserStore((state) => state.companyInfo);
  const openDialog = useDialogStore((state) => state.openDialog);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);

  const { data: rewardData } = useGetRewardList({ enabled: !!rewardId, id: rewardId });

  const currentReward = rewardData?.Data?.data?.[0] || {};
  const imageURL = currentReward?.image?.[0] || '';
  const isEmpty = currentReward && currentReward.stock === 0;

  const isOwner = useMemo(() => {
    if (!userInfo || !companyInfo) return false;
    return userInfo._id === companyInfo.user_id._id;
  }, [companyInfo, userInfo]);

  const buttonText = useMemo(() => {
    if (isEmpty) return 'สินค้าหมดชั่วคราว';
    if (!isOwner) return 'สิทธิ์การแลกเป็นของท่านอื่น';
    return 'เพิ่มลงตะกร้า';
  }, [isOwner, isEmpty]);

  const handleRedeemDialogClick = () => {
    const isTerminated = companyInfo?.state === 'ACCOUNT TERMINATE';
    const hasNoAddress = !companyInfo?.address?.length;

    if (isTerminated) {
      openDialog({
        title: 'ไม่สำเร็จ',
        message: `กรุณาติดต่อ ฝ่ายขายแผนกบัตร\nคาลเท็กซ์สตาร์การ์ด`,
        variant: 'error',
      });
      return;
    }

    if (hasNoAddress) {
      navigate('/address/add');
      return;
    }

    setRedeemDialogOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{currentReward?.name || 'Reward Detail'} | Caltex StarCard</title>
      </Helmet>

      <Box>
        <RewardDetailCard image={imageURL} />
        <RewardDetailInfo
          point={currentReward?.point || '0'}
          name={currentReward?.name || '-'}
          detail={currentReward?.detail || '-'}
          quantity={currentReward?.quantity || '-'}
          unit={currentReward?.unit || 'ชิ้น'}
          value={
            typeof currentReward?.value === 'number'
              ? String(currentReward?.value).replace(/\d{1,3}(?=(\d{3})+(?!\d))/g, '$&,')
              : '-'
          }
        />

        {/* Consent */}
        <Typography
          sx={{
            fontSize: '1.2rem',
            lineHeight: '1.5rem',
            color: '#004B61',
            textAlign: 'center',
            padding: '10px 16px 0',
          }}
        >
          ของรางวัลอาจมีการเปลี่ยนแปลง สี หรือ รุ่น ในกรณีที่ของรางวัลหมด / เลิกผลิต
          เป็นรุ่นใกล้เคียงหรือเทียบเท่า
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 10px 70px',
          }}
        >
          <Link to="/reward" style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIosIcon sx={{ width: '14px', color: 'white' }} />}
              sx={{
                width: '100px',
                height: '40px',
                borderRadius: '6px',
                color: 'white',
                backgroundColor: '#005972',
                fontSize: '1.25rem',

                '&:hover': {
                  color: 'white',
                  backgroundColor: '#005972',
                  opacity: '.85',
                },
              }}
            >
              กลับ
            </Button>
          </Link>
          <Button
            onClick={handleRedeemDialogClick}
            disabled={isEmpty || !isOwner}
            sx={{
              miniWidth: '150px',
              height: '40px',
              padding: '0 20px',
              borderRadius: '6px',
              color: 'white',
              backgroundColor: '#E1000C',
              fontSize: '1.25rem',

              '&:disabled': {
                color: '#eee',
              },

              '&:hover': {
                color: 'white',
                backgroundColor: '#E1000C',
                opacity: '.85',
              },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      </Box>

      <RedeemDialog
        open={redeemDialogOpen}
        onClose={() => setRedeemDialogOpen(false)}
        reward={currentReward}
      />
    </>
  );
};

export default RewardDetailPage;
